/**
 * AD-195: live simulation state for the public page.
 *
 * A race being replayed by an RD (race director) via the checkin-web
 * "Simulation" panel has exactly one row in `race_simulations`. This hook
 * mirrors that row into React state for the public-live spectator page:
 *   - Initial fetch on mount (and whenever raceId changes).
 *   - A Realtime subscription on `race_simulations`, filtered to this race,
 *     so play/pause/speed/teleport changes made by the RD show up here
 *     within the Realtime latency window (no polling needed for the STATE
 *     itself — only the runner-list refetch, wired in LiveRacePageV2, needs
 *     a timer, because newly-visible checkin_events rows don't fire their
 *     own Realtime event).
 *
 * DELETE on the row means the RD exited simulation mode entirely — the race
 * goes back to being a normal live (or completed) race, so state resets to
 * null and every consumer (the header clock, the chip) falls back to
 * showing nothing / real wall-clock time.
 *
 * This hook does NOT filter checkin_events. That truncation is enforced
 * entirely server-side by the anon RLS policy on checkin_events (Task 1:
 * `server_timestamp <= sim_now(race_id)`). This hook only reads the state
 * row to power the clock display and to know WHEN to ask the runner list to
 * refetch (see LiveRacePageV2.tsx).
 */
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { fetchSimulationState } from '../lib/queries';
import type { SimulationState } from '@as-checkin/shared';

export function useSimulation(raceId: string | number | null): { simulation: SimulationState | null } {
  const [simulation, setSimulation] = useState<SimulationState | null>(null);

  useEffect(() => {
    if (!raceId) return;

    // Initial fetch — populates the clock/chip immediately on load without
    // waiting for a Realtime event (there may never be one, if the race
    // isn't being simulated, or if it's mid-simulation and nothing changes
    // for a while).
    void fetchSimulationState(raceId).then(setSimulation);

    // Subscribe to INSERT/UPDATE/DELETE on this race's simulation row.
    // Pattern copied from useRaceData.ts's race-metadata channel.
    const channel = supabase
      .channel(`race-${raceId}-simulation`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'race_simulations', filter: `race_id=eq.${raceId}` },
        (payload) => {
          if (payload.eventType === 'DELETE') {
            // RD exited simulation mode — back to a normal race.
            setSimulation(null);
            return;
          }
          // INSERT or UPDATE: Realtime sends the new row's published columns.
          // Map explicitly (rather than trusting the payload shape) since
          // `speed` is NUMERIC in Postgres and can arrive as a string.
          const row = payload.new as Record<string, unknown>;
          setSimulation({
            anchor_sim_time: String(row.anchor_sim_time),
            anchor_wall_time: String(row.anchor_wall_time),
            speed: Number(row.speed),
            playing: Boolean(row.playing),
          });
        }
      )
      .subscribe();

    // Cleanup: unsubscribe when raceId changes or the component unmounts,
    // so we don't leak channels or keep updating state after teardown.
    return () => { void supabase.removeChannel(channel); };
  }, [raceId]);

  return { simulation };
}
